import React, { useState, useEffect, useCallback } from "react";
import { MessageSquare, Send, User as UserIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getJobComments, postJobComment } from "../../../../../services/staffingService";

const fmtWhen = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const ActivityTab = ({ providerCompanyId, jobId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getJobComments(providerCompanyId, jobId);
      // Sort most recent first
      const list = [...(data?.comments || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setComments(list);
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId, jobId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handlePost = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await postJobComment(providerCompanyId, jobId, text);
      setDraft("");
      await fetchComments();
    } catch (err) {
      console.error("Error posting comment:", err);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      <form
        onSubmit={handlePost}
        className="rounded-xl border border-border bg-card p-4 shadow-sm"
      >
        <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <MessageSquare size={14} />
          Add a comment
        </label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Share an update, ask a question, or flag an issue…"
          rows={3}
          className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-pink focus:outline-none focus:ring-1 focus:ring-brand-pink"
        />
        <div className="mt-2 flex justify-end">
          <button
            type="submit"
            disabled={!draft.trim() || posting}
            className="flex items-center gap-2 rounded-lg bg-brand-pink px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-pink/90 disabled:opacity-50"
          >
            <Send size={14} />
            {posting ? "Posting…" : "Post"}
          </button>
        </div>
      </form>

      {/* Timeline */}
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-muted/50 px-4 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Activity Timeline ({comments.length})
          </h3>
        </div>

        {loading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : comments.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No activity yet. Be the first to post a comment.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            <AnimatePresence initial={false}>
              {comments.map((c, idx) => {
                const user = c.provider_manager_id?.user_id;
                const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";
                return (
                  <motion.li
                    key={c._id || idx}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.18 }}
                    className="flex gap-3 px-4 py-3"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-semibold text-primary">
                      {user?.profilePictureUrl ? (
                        <img src={user.profilePictureUrl} alt={name} className="h-full w-full object-cover" />
                      ) : (
                        <span>{initials(name)}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-card-foreground">{name}</span>
                        <span className="text-[11px] text-muted-foreground">{fmtWhen(c.createdAt)}</span>
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-card-foreground">{c.text}</p>
                      {c.attachmentUrl && (
                        <a
                          href={c.attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-xs text-brand-pink hover:underline"
                        >
                          View attachment
                        </a>
                      )}
                    </div>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
};

export default ActivityTab;
